import { CommonModule } from '@angular/common';
import { Component, Input, OnInit } from '@angular/core';
import { RequestBudgetSupportItemComponent } from 'src/app/pages/agreements/components/request-budget-support-item/request-budget-support-item.component';
import { RateServiceGroupData } from 'src/app/pages/agreements/types/rate-service-group-form.model';
import { BannerComponent } from 'src/app/shared/components/banner/banner.component';
import { EligibleHourlyRateService } from 'src/app/shared/graphql/agreements/get-eligible-services-for-agreement-query.graphql';
import { ServiceCategoryableType } from 'src/app/shared/graphql/graphql-types';
import { BudgetSupportType } from 'src/app/shared/models/gql/rate.model';

@Component({
  standalone: true,
  imports: [CommonModule, BannerComponent, RequestBudgetSupportItemComponent],
  selector: 'app-service-categories-applied-banner',
  templateUrl: './service-categories-applied-banner.component.html',
  styleUrls: ['./service-categories-applied-banner.component.scss'],
})
export class ServiceCategoriesAppliedBannerComponent implements OnInit {
  // all support types available to the agreement rates
  @Input() availableBudgetSupportTypes: BudgetSupportType[];

  // default budget support types
  @Input() defaultBudgetSupportTypes: BudgetSupportType[] = [];

  // specialised support types - support types that can only be added as available by Mable admin team
  @Input() specialisedBudgetSupportTypes?: BudgetSupportType[] = [];

  // flag to indicate whether support types have already been added to the rates of an exising agreement
  @Input() isServicesAllocated: boolean;

  // flag to indicate whether Mable admin team has added any specialised support type as available for a specific client but SP has not selected it for any of the rates
  @Input() isSpecialisedServicesAvailable: boolean;

  // service groups form group from structured rates
  @Input() serviceGroups: RateServiceGroupData[];

  // eligible hourly services for agreements with structured rates
  @Input() set eligibleServices(services: EligibleHourlyRateService[]) {
    this.setBudgetSupportTypesFromEligibleServices(services);
    this.initialiseProperties(services);
    this.setAppliedBudgetSupportTypesAndOtherBudgetSupportTypesAvailable();
  }

  // client first name
  @Input() clientFirstName: string;

  // consumer uuid
  @Input() consumerUuid: string;

  // support provider uuid
  @Input() supportProviderUuid: string;

  // support types that have been added to the agreement rates (will be displayed in the 1st list)
  appliedBudgetSupportTypes: BudgetSupportType[];

  // other support types available to the agreement rates (will be displayed in the 2nd list)
  otherBudgetSupportTypesAvailable: BudgetSupportType[];

  // flag to indicate whether the flag should be displayed
  shouldDisplayBanner = true;

  constructor() {}

  ngOnInit(): void {
    this.setAppliedBudgetSupportTypesAndOtherBudgetSupportTypesAvailable();
  }

  private initialiseProperties(eligibleServices: EligibleHourlyRateService[]): void {
    if (!this.serviceGroups?.length) {
      return;
    }

    this.isServicesAllocated = this.isNdisServicesAssigned();
    this.isSpecialisedServicesAvailable = this.isSpecialisedServicesUnassigned(eligibleServices);
    this.shouldDisplayBanner = !this.isServicesAllocated || this.isSpecialisedServicesAvailable;
  }

  /**
   * Checks whether any specialised NDIS service categories are available but have yet to be added to a service group.
   *
   * @param eligibleServices - The list of eligible services for the agreement.
   * @returns true when any specialised NDIS service categories have not yet been added to a service group.
   */
  private isSpecialisedServicesUnassigned(eligibleServices: EligibleHourlyRateService[]): boolean {
    const specialisedServiceCategories = eligibleServices.filter((supportType) => supportType.isAdminOnlyType);

    if (!specialisedServiceCategories?.length) {
      return false;
    }
    const assignedServiceCategoryIds = this.serviceGroups.flatMap((serviceGroup) =>
      serviceGroup.selectedServices.map((service) => service.serviceCategoryableId),
    );
    return specialisedServiceCategories.some(
      (specialisedServiceCategory) => !assignedServiceCategoryIds.includes(Number(specialisedServiceCategory.id)),
    );
  }

  /**
   * Checks whether any NDIS service categories have been added to a service groups.
   *
   * @returns true if any NDIS service categories have been added to a service groups.
   */
  private isNdisServicesAssigned(): boolean {
    return this.serviceGroups.some((serviceGroup) =>
      serviceGroup.selectedServices.some(
        (service) => service.serviceCategoryableType === ServiceCategoryableType.NdisServiceCategory,
      ),
    );
  }

  /**
   * Defines 1st and 2nd list items that will be displayed in the banner.
   */
  private setAppliedBudgetSupportTypesAndOtherBudgetSupportTypesAvailable(): void {
    if (!this.availableBudgetSupportTypes?.length) {
      return;
    }

    const availableBudgetSupportTypesIds = this.availableBudgetSupportTypes.map((supportType) => supportType.id);
    let otherBudgetSupportTypesAvailableIds = [];

    if (this.isServicesAllocated === false) {
      this.appliedBudgetSupportTypes = this.defaultBudgetSupportTypes;
      // include all available support types but the default support types
      const defaultBudgetSupportTypesIds = this.defaultBudgetSupportTypes.map((supportType) => supportType.id);
      otherBudgetSupportTypesAvailableIds = availableBudgetSupportTypesIds.filter(
        (supportTypeId) => !defaultBudgetSupportTypesIds.includes(supportTypeId),
      );
      this.otherBudgetSupportTypesAvailable = this.availableBudgetSupportTypes.filter((supportType) =>
        otherBudgetSupportTypesAvailableIds.includes(supportType.id),
      );
    } else if (this.isSpecialisedServicesAvailable) {
      this.appliedBudgetSupportTypes = this.specialisedBudgetSupportTypes;
      // include all available support types but the specialised support types
      const specialisedBudgetSupportTypesIds = this.specialisedBudgetSupportTypes.map((supportType) => supportType.id);
      otherBudgetSupportTypesAvailableIds = availableBudgetSupportTypesIds.filter(
        (supportTypeId) => !specialisedBudgetSupportTypesIds.includes(supportTypeId),
      );
      this.otherBudgetSupportTypesAvailable = this.availableBudgetSupportTypes.filter((supportType) =>
        otherBudgetSupportTypesAvailableIds.includes(supportType.id),
      );
    }
  }

  private setBudgetSupportTypesFromEligibleServices(services: EligibleHourlyRateService[]): void {
    if (!services?.length) {
      return;
    }

    const defaultBudgetSupportTypes: BudgetSupportType[] = [];
    const specialisedBudgetSupportTypes: BudgetSupportType[] = [];
    this.availableBudgetSupportTypes = services.map((service) => {
      if (service.defaultService) {
        defaultBudgetSupportTypes.push({
          id: service.id,
          displayName: service.displayName,
          isAdminOnlyType: service.isAdminOnlyType,
        });
      }

      if (service.isAdminOnlyType) {
        specialisedBudgetSupportTypes.push({
          id: service.id,
          displayName: service.displayName,
          isAdminOnlyType: service.isAdminOnlyType,
        });
      }

      return {
        id: service.id,
        displayName: service.displayName,
        isAdminOnlyType: service.isAdminOnlyType,
      };
    });

    this.defaultBudgetSupportTypes = defaultBudgetSupportTypes;
    this.specialisedBudgetSupportTypes = specialisedBudgetSupportTypes;
  }
}
